import {
    Anchor,
    Avatar,
    Box,
    Divider,
    Icon,
    MediaObject,
    Paragraph,
    Tag,
    Text,
} from '@zillow/constellation-10';
import { IconAwardRibbonFilled } from '@zillow/constellation-icons';
import React from 'react';

import type { Contributor } from '../../B2CPostTemplate.types';

/**
 * Narrows `expertise` to `string[]` before it is mapped over.
 *
 * Looks redundant against `Contributor.expertise?: string[]`, but that type is
 * an assertion about the WordPress payload, not a runtime guarantee — the field
 * is unvalidated CMS data, so a non-array (or an array of non-strings) would
 * otherwise reach `.map()` and throw during SSR.
 */
const isExpertiseArray = (expertise: unknown): expertise is string[] =>
    Array.isArray(expertise) &&
    expertise.every((item) => typeof item === 'string');

/**
 * Contents of the contributor popover: avatar, name, job title, expertise tags,
 * a truncated bio, and a "Read more" link.
 *
 * Rendered inside Popover.Body (see ContributorsCard), so it deliberately owns
 * no positioning, width, or padding — v10's popoverContent recipe supplies all
 * of that, at the same values v8's PopoverMixin did (maxWidth 360, maxHeight
 * 320, padding 16, radius 12).
 */
const ContributorPopover: React.FC<{ contributor: Contributor }> = ({
    contributor,
}) => (
    <Box>
        {/* v8 MediaObject had `align` and `gutter` props; v10 has neither — its
            Root recipe is a plain flex row ({ display: flex, alignItems: start,
            gap: default }) and everything is done through css.

            - v8 align="center" → alignItems: 'center'.
            - v8 gutter defaulted to "sm" (16px) = v10's default gap (16px), so
              the spacing itself needs no change. The ternary is not cosmetic:
              v8's MediaObject only rendered the Media wrapper when `media` was
              truthy (drywall's renderLayout does `{media && <Media>}`), so a
              contributor with no avatar got no gutter at all. v10 always renders
              MediaObject.Media, and because the spacing moved from the Media
              element's margin-right onto the Root's `gap`, the empty div still
              opens a 16px gap. Collapsing the gap when there is no avatar keeps
              the text flush left exactly as in v8. */}
        <MediaObject
            css={{
                alignItems: 'center',
                gap: contributor.avatar ? 'default' : 'none',
                // Clears the absolutely positioned close button, as
                // Popover.Header does; header={null} leaves the body unpadded.
                paddingInlineEnd: 'layout.looser',
            }}
            media={
                contributor.avatar ? (
                    // The image goes through src/alt rather than an <Image>
                    // child: v10 Avatar only renders its internal Avatar.Image
                    // slot (which carries the circular clip) when children are
                    // empty — an <Image> child renders unclipped. Same reasoning
                    // and the same two overrides as AuthorAvatar.tsx: v10's
                    // avatarRecipe paints a 1px inset ring that v8 never had and
                    // offers no prop to opt out of, and v10's `lg` grew from
                    // v8's 64px to 72px, so the v8 size is pinned back.
                    //
                    // overflow: 'hidden' restores a third v8 behavior: v8's
                    // AvatarMixin clipped at the Avatar root, while v10 only clips
                    // inside the Avatar.Image slot. Author avatars are often dead
                    // URLs, and a broken <img> renders the browser's fallback icon
                    // plus the full `alt` text, which the img's own overflow does
                    // not clip — so the name spills out of the circle over the
                    // popover content. Same fix as ContributorsCard.tsx.
                    <Avatar
                        size="lg"
                        src={contributor.avatar}
                        alt={contributor.name}
                        css={{
                            boxShadow: 'none',
                            overflow: 'hidden',
                            width: '64px',
                            height: '64px',
                            // `display: block` blockifies the img so the 100%
                            // below still applies when the URL is dead. A broken
                            // <img> with alt text is a non-replaced inline box,
                            // which width/height do not apply to, so it grows to
                            // the alt text instead of filling the avatar. v8 was
                            // clamped by this repo's globalCss `img, video {
                            // max-width: 100% }`; v10's Avatar.Image carries the
                            // `c11n` class, whose `all: unset` outranks that rule
                            // inside `@layer base`. Same fix as
                            // ContributorsCard.tsx; no effect once the image
                            // loads.
                            '& img': {
                                display: 'block',
                                inlineSize: '100%',
                                blockSize: '100%',
                            },
                        }}
                    />
                ) : null
            }
        >
            <Box
                css={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                {/* v8 fontType="bodyHeading" (16px/700) → textStyle="body-bold".
                    No color needed: v8 Text defaulted to fontColor="textDark"
                    (#111116) and v10 Text defaults to color="text.neutral",
                    which is the same value. */}
                <Text textStyle="body-bold">{contributor.name}</Text>
                {contributor.jobTitle && (
                    // v8 fontType="bodySmall" (14px) → textStyle="body-sm";
                    // fontColor="textMedium" (#535364) → text.subtle (gray.600,
                    // same hex). Style props are gone in v10, so the color moves
                    // into css.
                    <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                        {contributor.jobTitle}
                    </Text>
                )}
            </Box>
        </MediaObject>
        {/* v8 marginY="xs" (8px) → css marginBlock: 'tighter' (8px). v10's
            default tone="muted" resolves to gray.300 (#CDCDD3), which is the v8
            divider color exactly, so no tone override is needed. */}
        <Divider css={{ marginBlock: 'tighter' }} />
        {isExpertiseArray(contributor.expertise) &&
            contributor.expertise.length > 0 && (
                <>
                    {/* v8 gutter="xxs" (4px) → gap: 'tightest' (4px). v10's
                        default alignItems: 'start' already matches v8's default
                        align, so only the gap is overridden. Media is always
                        present here (the icon), so there is no gap ternary. */}
                    <MediaObject
                        css={{ gap: 'tightest' }}
                        media={
                            // v10 icons from @zillow/constellation-icons are raw
                            // SVGs with no size/fontColor/spacing props, so they
                            // are wrapped in v10 <Icon> and styled through css.
                            //
                            // v8 IconAwardRibbon → v10 IconAwardRibbonFilled:
                            // the base name changed meaning between versions.
                            // v8 shipped IconAwardRibbon (a single solid path —
                            // the FILLED glyph) alongside IconAwardRibbonOutline;
                            // v10 exports only ...Filled and ...Outline, so
                            // Filled is the like-for-like mapping. Same call as
                            // AuthorPage/Expertise.tsx.
                            //
                            // The icon size scale shifted one step: v8 size="xs"
                            // is 16px, and v10's smallest fixed size, sm, is also
                            // 16px (v10 has no "xs" on the Icon size prop).
                            //
                            // v8 fontColor="blue500" is SkunkWorks #0033AB =
                            // v10 blue.700 exactly. Note the trap: v10 blue.500
                            // is #3574FF, a completely different color. Kept on
                            // the primitive so this accent does not invert in
                            // dark mode, matching how Expertise.tsx handles the
                            // same heading. v8 marginTop="xxs" → 'tightest'.
                            <Icon
                                size="sm"
                                css={{
                                    color: 'blue.700',
                                    marginTop: 'tightest',
                                }}
                            >
                                <IconAwardRibbonFilled />
                            </Icon>
                        }
                    >
                        <Paragraph
                            textStyle="body-bold"
                            css={{ color: 'blue.700' }}
                        >
                            Expertise
                        </Paragraph>
                    </MediaObject>
                    <Box
                        css={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            // v8 $spacing.xs (8px) → 'tighter';
                            // v8 $spacing.sm (16px) → 'default'.
                            gap: 'tighter',
                            marginTop: 'default',
                            marginBottom: 'default',
                        }}
                    >
                        {/* v8 appearance="info" → v10 tone="info", but the
                            mapping does not survive the theme: v10's
                            Tag.info.bg resolves to bg-feedback-info-soft
                            (#DDDDE1) and Tag.info.text to text-neutral
                            (#111116) — i.e. identical to tone="gray" — so the
                            pills render gray where v8 had a blue tint
                            (bg #DCEDFF / text #001962). No v10 tone reproduces
                            it (tone="blue" is #9ACDFF, far more saturated), so
                            the palette is pinned back to the blue ramp.

                            Careful: the ramps are offset ~1 step, so matching by
                            step number is wrong. v8's #DCEDFF is v8 blue200, but
                            v10 blue.200 is #C5E2FF (off by 23,11,0 and visibly
                            more saturated); v10 blue.100 is #E2F0FF (off by
                            6,3,0). So blue.100 is the correct token. blue.900 is
                            #001962 in both. Primitives, not semantic tokens, so
                            the pills do not invert in dark mode.

                            tone must NOT be carried over as "info". In v10, info
                            is a *status* tone, and Tag force-renders a ToneIcon
                            for status tones at size md — see tag-icon.tsx: "Design
                            would like for us to 'force' the icon type if the tone
                            is a status." It is not suppressible; the `icon` prop
                            only adds, and `isStatusTone` short-circuits before
                            children are read. v8's appearance="info" was colour
                            only (v8 Tag rendered an icon solely when you passed
                            one), so keeping tone="info" put a stray ⓘ inside every
                            expertise pill. These are category labels, not
                            feedback, so the default non-status tone="gray" is the
                            correct mapping — and since the palette above is pinned
                            explicitly, dropping the tone changes nothing visually
                            beyond removing the icon.

                            v8 needed textTransform="none" because v8 Tag
                            uppercased by default; v10 Tag does not, so the prop
                            is simply dropped rather than translated. v8's
                            default size md (bodySmallHeading, 14px/700) is v10's
                            default size md (body-sm-bold, 14px/700), so size is
                            left alone. The one remaining delta is the pill
                            radius: v8 was 12px, v10 is fully rounded. */}
                        {contributor.expertise.map((item) => (
                            <Tag
                                key={item}
                                css={{
                                    backgroundColor: 'blue.100',
                                    color: 'blue.900',
                                }}
                            >
                                {item}
                            </Tag>
                        ))}
                    </Box>
                </>
            )}
        {contributor.description && (
            // Rendered with dangerouslySetInnerHTML so that HTML links in the
            // WordPress bio (e.g. <a href="\u2026">) become real anchors instead of
            // literal tag text. CSS line clamping replaces string-slice
            // truncation: slicing at a fixed character count can split an <a>
            // tag in two, producing broken markup; clamping at the rendered
            // line level is safe regardless of HTML content.
            // @ts-expect-error - Text requires children but we use dangerouslySetInnerHTML
            <Text
                textStyle="body-sm"
                css={{
                    color: 'text.neutral',
                    textAlign: 'left',
                    display: '-webkit-box',
                    WebkitLineClamp: '4',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    // If the bio contains a link positioned past the 4th visual
                    // line, that link is still in DOM focus order even while
                    // hidden by overflow:hidden. Expand the container when any
                    // descendant is focused so keyboard users can see and
                    // activate the link.
                    _focusWithin: {
                        display: 'block',
                        overflow: 'visible',
                    },
                    '& a': { color: 'text.link.default' },
                    '& a:hover': { color: 'text.link.hover' },
                }}
                dangerouslySetInnerHTML={{ __html: contributor.description }}
            />
        )}
        {contributor.link && (
            <Box css={{ marginTop: 'tighter' }}>
                {/* v8 Text had an `as` prop; v10 uses asChild + the real
                    element. v8 fontColor="brand" is SkunkWorks #0041D9 =
                    v10 blue.600 = text.accent.brand.hero. It only ever styled
                    the wrapper — v8's Anchor set its own link color, and so does
                    v10's (anchorRecipe base color: Anchor.default) — but it is
                    carried across so the mapping is not silently lost. The bold
                    weight does reach the link: v10 anchorRecipe sets
                    textStyle: 'inherit'. */}
                <Text
                    asChild
                    textStyle="body-bold"
                    css={{ color: 'text.accent.brand.hero' }}
                >
                    <p>
                        <Anchor href={contributor.link} target="_blank">
                            Read more
                        </Anchor>
                    </p>
                </Text>
            </Box>
        )}
    </Box>
);

export default ContributorPopover;
